import React, { useEffect, useMemo, useRef, useState } from 'react'
import Validate from './validation.js'
import FieldErrorMessage from './field_error_message.js'
import ReCAPTCHA from 'react-google-recaptcha'
import EquipmentItemFields from './equipment_item_fields.js'
import {
  DURATION_UNITS,
  HIRE_LOCATIONS,
  buildCategoryOptions,
  buildProductsByCategory,
  isCatalogItemValid,
  prefillItemFromEquipment,
} from '../data/booking_catalog.js'
import { QUOTE_SUBMISSION_KEY, goToFormConfirmation } from '../data/form_storage.js'
import {
  RECAPTCHA_SITE_KEY as SITE_KEY,
  getRecaptchaToken,
  recaptchaErrorMessage,
  shouldSkipRecaptchaClient,
} from '../utils/recaptcha.js'

const emptyItem = () => ({
  category: 'forklifts',
  type: '',
  model: '',
  qty: '1',
  duration_amount: '',
  duration_unit: '',
  delivery_address: '',
})

const emptyControls = () => ({
  full_name: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
  company_name: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
  email: { value: '', valid: false, touched: false, validationRules: { isRequired: true, isEmail: true }, errorMessages: '' },
  phone: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
  contact_method: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
  hire_location: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
  notes: { value: '', valid: true, touched: false, validationRules: {}, errorMessages: '' },
})

const itemFilled = (item) =>
  Boolean(
    item.duration_amount &&
      Number(item.duration_amount) > 0 &&
      item.duration_unit &&
      item.delivery_address
  )

const areItemsValid = (items, catalog) =>
  items.every((item) => isCatalogItemValid(item, catalog) && itemFilled(item))

const areControlsValid = (controls) =>
  Object.values(controls).every((control) => control.valid)

export default function QuoteForm() {
  const recaptchaRef = useRef(null)
  const formErrorRef = useRef(null)
  const [items, setItems] = useState([emptyItem()])
  const [itemsError, setItemsError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [clientReady, setClientReady] = useState(false)
  const [recaptchaReady, setRecaptchaReady] = useState(() => shouldSkipRecaptchaClient() || !SITE_KEY)
  const [formValues, setFormValues] = useState({
    formErrors: '',
    formControls: emptyControls(),
  })

  const categoryOptions = useMemo(() => buildCategoryOptions(), [])
  const productsByCategory = useMemo(() => buildProductsByCategory(), [])

  useEffect(() => {
    setClientReady(true)
  }, [])

  const appliedPrefill = useRef(false)
  useEffect(() => {
    if (appliedPrefill.current || typeof window === 'undefined') return
    const equipment = new URLSearchParams(window.location.search).get('equipment') || ''
    const prefill = prefillItemFromEquipment(equipment)
    if (!prefill) return
    appliedPrefill.current = true
    setItems((prev) =>
      prev.map((item, i) => (i === 0 ? { ...item, ...prefill } : item))
    )
  }, [])

  const formIsValid = useMemo(
    () =>
      areControlsValid(formValues.formControls) &&
      areItemsValid(items, productsByCategory),
    [formValues.formControls, items, productsByCategory]
  )

  const showFormError = (message) => {
    setFormValues((prev) => ({ ...prev, formErrors: message }))
    requestAnimationFrame(() => {
      formErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }

  const changeHandler = (e) => {
    const { name, value, type } = e.target
    setFormValues((prev) => {
      const updatedControls = { ...prev.formControls }
      const updatedFormElement = { ...updatedControls[name] }
      updatedFormElement.value = type === 'radio' ? value : value
      updatedFormElement.touched = true
      const validateFormElement = Validate(updatedFormElement.value, updatedFormElement.validationRules)
      updatedFormElement.valid = validateFormElement.isValid
      updatedFormElement.errorMessages = validateFormElement.errorMessages
      updatedControls[name] = updatedFormElement
      return {
        ...prev,
        formControls: updatedControls,
        formErrors: '',
      }
    })
  }

  const updateItem = (index, field, value) => {
    setItemsError('')
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item
        const updated = { ...item, [field]: value }
        if (field === 'category') {
          updated.type = ''
          updated.model = ''
          updated.qty = '1'
        }
        if (field === 'type') {
          updated.model = ''
          updated.qty = '1'
        }
        return updated
      })
    )
  }

  const addItem = () => {
    setItems((prev) => {
      const source = [...prev].reverse().find(itemFilled) || prev[0] || emptyItem()
      return [
        ...prev,
        {
          ...emptyItem(),
          duration_amount: source.duration_amount || '',
          duration_unit: source.duration_unit || '',
          delivery_address: source.delivery_address || '',
        },
      ]
    })
  }

  const touchAndValidateControls = (controls) => {
    const updated = {}
    for (const name of Object.keys(controls)) {
      const el = { ...controls[name], touched: true }
      const result = Validate(el.value, el.validationRules)
      el.valid = result.isValid
      el.errorMessages = result.errorMessages
      updated[name] = el
    }
    return updated
  }

  const submitHandler = async (e) => {
    e.preventDefault()
    if (submitting) return

    const updatedControls = touchAndValidateControls(formValues.formControls)
    const controlsOk = areControlsValid(updatedControls)
    const itemsOk = areItemsValid(items, productsByCategory)

    setFormValues((prev) => ({
      ...prev,
      formControls: updatedControls,
      formErrors: '',
    }))
    setItemsError(itemsOk ? '' : 'Please complete category, type, size, hire duration and delivery address for each item.')

    if (!controlsOk || !itemsOk) {
      setFormValues((prev) => ({
        ...prev,
        formControls: updatedControls,
        formErrors: 'Please complete all required fields marked with *.',
      }))
      requestAnimationFrame(() => {
        formErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
      return
    }

    let recaptchaToken = 'dev-bypass'
    try {
      recaptchaToken = await getRecaptchaToken(recaptchaRef)
    } catch (err) {
      showFormError(recaptchaErrorMessage(err))
      return
    }

    setSubmitting(true)

    try {
      const fieldData = new FormData()
      for (let formElementId in updatedControls) {
        fieldData.append(formElementId, updatedControls[formElementId].value)
      }

      const first = items[0] || emptyItem()
      fieldData.append('duration_amount', first.duration_amount)
      fieldData.append('duration_unit', first.duration_unit)
      fieldData.append('hire_duration', `${first.duration_amount} ${first.duration_unit}`.trim())
      fieldData.append('delivery_address', first.delivery_address)
      fieldData.append('items', JSON.stringify(items))
      fieldData.append('recaptcha', recaptchaToken)

      const res = await fetch('/api/quote', {
        method: 'POST',
        body: fieldData,
      })

      if (res.status === 200) {
        const summary = {
          full_name: updatedControls.full_name.value,
          company_name: updatedControls.company_name.value,
          email: updatedControls.email.value,
          phone: updatedControls.phone.value,
          contact_method: updatedControls.contact_method.value,
          hire_location: updatedControls.hire_location.value,
          notes: updatedControls.notes.value,
          items,
        }
        goToFormConfirmation('/quote-confirmation/', QUOTE_SUBMISSION_KEY, summary)
        return
      }

      const errors = await res.json().catch(() => ({ message: 'Something went wrong, please try again.' }))
      showFormError(errors.message || 'Something went wrong, please try again.')
    } catch (err) {
      showFormError('Something went wrong, please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const c = formValues.formControls

  return (
    <form className="hire_form" name="quote" onSubmit={submitHandler} noValidate>
      <div className="hire_form_banner">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 13h8v2H8v-2zm0 4h5v2H8v-2z" />
        </svg>
        <span>Get a Quote</span>
      </div>

      <label className="hire_field">
        <span className="hire_label">Full Name <span className="req">*</span></span>
        <input type="text" name="full_name" value={c.full_name.value} onChange={changeHandler} />
      </label>
      <FieldErrorMessage className={c.full_name.touched && !c.full_name.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.full_name.errorMessages} />

      <label className="hire_field">
        <span className="hire_label">Company Name <span className="req">*</span></span>
        <input type="text" name="company_name" value={c.company_name.value} onChange={changeHandler} />
      </label>
      <FieldErrorMessage className={c.company_name.touched && !c.company_name.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.company_name.errorMessages} />

      <label className="hire_field">
        <span className="hire_label">Email Address <span className="req">*</span></span>
        <input type="email" name="email" value={c.email.value} onChange={changeHandler} />
      </label>
      <FieldErrorMessage className={c.email.touched && !c.email.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.email.errorMessages} />

      <label className="hire_field">
        <span className="hire_label">Contact Number <span className="req">*</span></span>
        <input type="tel" name="phone" value={c.phone.value} onChange={changeHandler} />
      </label>
      <FieldErrorMessage className={c.phone.touched && !c.phone.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.phone.errorMessages} />

      <fieldset className="hire_radio_group">
        <legend>Preferred Contact Method <span className="req">*</span></legend>
        <label className="hire_radio">
          <input type="radio" name="contact_method" value="Phone" checked={c.contact_method.value === 'Phone'} onChange={changeHandler} />
          Phone
        </label>
        <label className="hire_radio">
          <input type="radio" name="contact_method" value="Email" checked={c.contact_method.value === 'Email'} onChange={changeHandler} />
          Email
        </label>
      </fieldset>
      <FieldErrorMessage className={c.contact_method.touched && !c.contact_method.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.contact_method.errorMessages} />

      <label className="hire_field">
        <span className="hire_label">Hire Location <span className="req">*</span></span>
        <select name="hire_location" value={c.hire_location.value} onChange={changeHandler}>
          <option value="" disabled>Select Auckland, Waikato or Christchurch</option>
          {HIRE_LOCATIONS.map((loc) => (
            <option key={loc} value={loc}>{loc}</option>
          ))}
        </select>
      </label>
      <FieldErrorMessage className={c.hire_location.touched && !c.hire_location.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.hire_location.errorMessages} />

      {items.map((item, index) => (
        <EquipmentItemFields
          key={index}
          item={item}
          index={index}
          categoryOptions={categoryOptions}
          productsByCategory={productsByCategory}
          updateItem={updateItem}
          showForkliftSizeHelp
        >
          <div className="hire_field">
            <span className="hire_label">Hire Duration <span className="req">*</span></span>
            <div className="hire_duration_row">
              <input
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                placeholder="Number"
                value={item.duration_amount}
                onChange={(e) => updateItem(index, 'duration_amount', e.target.value)}
              />
              <select
                value={item.duration_unit}
                onChange={(e) => updateItem(index, 'duration_unit', e.target.value)}
              >
                <option value="" disabled>Days / Weeks / Months</option>
                {DURATION_UNITS.map((unit) => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>
          </div>

          <label className="hire_field">
            <span className="hire_label">Delivery Address <span className="req">*</span></span>
            <input
              type="text"
              value={item.delivery_address}
              onChange={(e) => updateItem(index, 'delivery_address', e.target.value)}
            />
          </label>
        </EquipmentItemFields>
      ))}
      {itemsError ? <p className="hire_form_error">{itemsError}</p> : null}

      <button type="button" className="hire_add_item" onClick={addItem}>
        ADD ANOTHER ITEM FOR HIRE
      </button>

      <label className="hire_field">
        <span className="hire_label">Notes</span>
        <textarea name="notes" rows="4" value={c.notes.value} onChange={changeHandler} />
      </label>

      {SITE_KEY && clientReady && !shouldSkipRecaptchaClient() ? (
        <ReCAPTCHA
          size="invisible"
          ref={recaptchaRef}
          sitekey={SITE_KEY}
          asyncScriptOnLoad={() => setRecaptchaReady(true)}
        />
      ) : null}

      {formValues.formErrors ? (
        <p className="hire_form_error" ref={formErrorRef}>{formValues.formErrors}</p>
      ) : null}

      <button
        type="submit"
        disabled={submitting}
        className={`hire_submit${formIsValid ? '' : ' hire_submit_incomplete'}`}
      >
        {submitting ? 'Submitting…' : 'Submit'}
      </button>
    </form>
  )
}
